import { OverlayProvider } from "@toss/use-overlay";
import { ThemeProvider } from "@emotion/react";
import type { AppProps } from "next/app";
import { RecoilRoot } from "recoil";
import "@/styles/portal.css";
import "@/styles/reset.css";
import "@/styles/theme/font.css";
import "goi_common/src/design-system/tokens/typography.css";

import { naverPageView } from "@/utils/naverPageView";
import { ChakraProvider } from "@chakra-ui/react";
import { getUserId, HackleProvider } from "@hackler/react-sdk";

import AOS from "aos";
import "aos/dist/aos.css";
import {
  chakraTheme,
  cooKeys,
  getUserProperties,
  // customEvent,
  GlobalStyle,
  GoiDevTools,
  initUserProperties,
  setCookie,
  theme,
} from "goi_common";
import Head from "next/head";
import { Router, useRouter } from "next/router";
import Script from "next/script";
import { ReactElement, ReactNode, useEffect, useState } from "react";
import { Hydrate, QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import "slick-carousel/slick/slick.css";
import Layout from "@/components/Layout";
import { NextPage } from "next";
import { DialogRoot } from "@/components/Dialog/DialogRoot";
import PreviewServiceDialogRoot from "@/components/Packages/PreviewServiceDialogRoot";

import { HackleClient } from "@/utils/hackle";
import { markClientNavigated } from "@/utils/parlorBackTrapEntry";
import { GlobalModalRoot } from "@/components/Layout/GlobalModalRoot";

declare global {
  interface Window {
    kakao: any;
    naver: any;
    hackleClinet: any;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

// eslint-disable-next-line @typescript-eslint/ban-types
export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const router = useRouter();
  const [previous, setPrevious] = useState<string[]>([]);

  useEffect(() => {
    if (!router.asPath.startsWith("/login")) {
      const newPrevious = [router.asPath, ...previous].slice(0, 5);
      setPrevious(newPrevious);
      setCookie(cooKeys.previous, JSON.stringify(newPrevious));
    }
  }, [router.asPath]);

  useEffect(() => {
    if (router.query.from === "prime") {
      sessionStorage.setItem("from_prime", "true");
    }
  }, [router.query.from]);

  const googleTagManagerScript = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GTM}');
    `;

  const beusableScript = `
        (function(w, d, a){
            w.__beusablerumclient__ = {
                load : function(src){
                    var b = d.createElement("script");
                    b.src = src; b.async=true; b.type = "text/javascript";
                    d.getElementsByTagName("head")[0].appendChild(b);
                }
            };w.__beusablerumclient__.load(a + "?url=" + encodeURIComponent(d.URL));
        })(window, document, "//rum.beusable.net/load/b220104e212424u502");
    `;

  const facebookPixelScript = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '1159415875062121');
    fbq('track', 'PageView');
  `;

  const openaiPixelScript = `!function(w,d,s,u){if(w.oaiq)return;var q=function(){q.q.push(arguments)};q.q=[];w.oaiq=q;var j=d.createElement(s);j.async=1;j.src=u;var f=d.getElementsByTagName(s)[0];f.parentNode.insertBefore(j,f)}(window,document,"script","https://bzrcdn.openai.com/sdk/oaiq.min.js");oaiq("init",{pixelId:"BooyLNNAv4sbbxTVzaJynw",debug:true});`;

  const smartlogScript = `
    window.hpt_info = {'_account':'UHPT-301066','_server':'a300'};
    (function(d,s){
      var j=d.createElement(s);j.async=true;j.charset="utf-8";
      j.src="//cdn.smlog.co.kr/core/smart_renew.js";
      var f=d.getElementsByTagName(s)[0];f.parentNode.insertBefore(j,f);
    })(document,'script');
  `;

  const handleResize = () => {
    const vh = window.innerHeight * 0.01;

    document.documentElement.style.setProperty("--vh", `${vh}px`);
  };

  const handleRouteChange = (url: string) => {
    if (process.env.NEXT_PUBLIC_VERCEL_ENV === "production") {
      naverPageView();
    }
  };

  useEffect(() => {
    AOS.init();
    const goiMemberId = localStorage.getItem("goi-member-id");

    if (goiMemberId) setMemberId(goiMemberId);
  }, []);

  useEffect(() => {
    Router.events.on("routeChangeComplete", handleRouteChange);
    Router.events.on("routeChangeStart", markClientNavigated);

    return () => {
      Router.events.off("routeChangeComplete", handleRouteChange);
      Router.events.off("routeChangeStart", markClientNavigated);
    };
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [memberId, setMemberId] = useState("");

  useEffect(() => {
    if (HackleClient) {
      initUserProperties();
      let id = getUserId();

      if (memberId) {
        id = memberId;
      }

      const user = {
        userId: id,
        properties: getUserProperties(),
      };

      HackleClient.setUser(user);
    }
  }, [memberId]);

  const [isDev, setIsDev] = useState(false);

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_VERCEL_ENV !== "production") {
      setIsDev(true);
    }
  }, []);

  const Components = (
    <HackleProvider hackleClient={HackleClient} supportSSR>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <ChakraProvider resetCSS={true} theme={chakraTheme}>
          <QueryClientProvider client={queryClient}>
            <Hydrate state={pageProps.dehydratedState}>
              <RecoilRoot>
                <OverlayProvider>
                  <Layout>
                    <GlobalModalRoot>
                      <Component {...pageProps} />
                    </GlobalModalRoot>
                    <GoiDevTools />
                  </Layout>
                </OverlayProvider>
                <DialogRoot />
                <PreviewServiceDialogRoot />
              </RecoilRoot>

              <ReactQueryDevtools />
            </Hydrate>
          </QueryClientProvider>
        </ChakraProvider>
      </ThemeProvider>
    </HackleProvider>
  );

  return (
    <>
      {process.env.NEXT_PUBLIC_VERCEL_ENV === "production" && (
        <>
          <Script
            id="naver"
            strategy="afterInteractive"
            src="//wcs.naver.net/wcslog.js"
            onLoad={() => naverPageView()}
          />
          <Script
            id="gtm"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{ __html: googleTagManagerScript }}
          />
          <Script id="fb-pixel" dangerouslySetInnerHTML={{ __html: facebookPixelScript }} />
          <Script
            id="openai-pixel"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{ __html: openaiPixelScript }}
          />
          <Script
            id="smartlog"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{ __html: smartlogScript }}
          />
        </>
      )}
      <Script
        strategy="afterInteractive"
        id="beusable"
        type="text/javascript"
        dangerouslySetInnerHTML={{ __html: beusableScript }}
      />
      <Script
        strategy="afterInteractive"
        id="daum"
        type="text/javascript"
        charSet="UTF-8"
        src="//t1.daumcdn.net/kas/static/kp.js"
      />

      <Head>
        <title>
          {pageProps.pageOgTitle ? pageProps.pageOgTitle : "장례의 모든 것, 고이장례연구소"}
        </title>
        <meta
          name="description"
          content={
            pageProps.pageDescription
              ? pageProps.pageDescription
              : "막막한 상황, 고이가 함께 합니다. 장례식장, 장지(납골당) 추천부터 상조 서비스, 장례 상담, 임종 접수까지. 장례의 모든것, 고이"
          }
        />
        <meta
          property="og:title"
          content={pageProps.pageOgTitle ? pageProps.pageOgTitle : "장례의 모든 것, 고이장례연구소"}
        />
        <meta
          property="og:description"
          content={
            pageProps.pageDescription
              ? pageProps.pageDescription
              : "막막한 상황, 고이가 함께 합니다. 장례식장, 장지(납골당) 추천부터 상조 서비스, 장례 상담, 임종 접수까지. 장례의 모든것, 고이"
          }
        />

        {isDev && <meta name="robots" content="noindex, nofollow" />}
        {pageProps.pageCanonical && <link rel="canonical" href={pageProps.pageCanonical} />}
        {pageProps.pageSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(pageProps.pageSchema) }}
          />
        )}
      </Head>
      {Components}
    </>
  );
}

export default MyApp;
